import { useId } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Select } from 'antd';
import { ORG_UNIT_ELEVATIONS, actingOrgUnitIdOf, type OrgUnitDto, type OrgUnitElevation } from '@pams/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { ElevationMark } from './ElevationMark';
import { elevationSentence } from './elevationWords';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §11h, amended] A UNIT'S NODAL ACCESS LEVEL IS SET ON THE ORGANISATION PAGE, AND ONLY READ
 *  ON THE OFFICERS PAGE.
 *
 *  The setting belongs to the ORG UNIT — every nodal officer posted there acts one or two levels up
 *  — so its one editing control is on that unit's panel (`UnitElevationControl`). The Officers
 *  drawer shows it read-only (`UnitElevationReadout`) and says where it is set. The first build put
 *  the control in the officer drawer, which read as a setting about one officer, could not reach a
 *  unit with nobody posted yet, and gave the Organisation page no way even to see it.
 *
 *  ⚠️ `PUT /org-units/:id/elevation` behind `org.elevate` (SUPER_ADMIN) is the only writer; the
 *  control is offered on the same capability and everyone else reads the words. The API is the
 *  guard, this is courtesy.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const ROOT_SENTENCE = 'The top of the establishment has no level above it.';

/**
 * ⚠️ IT ASKS BEFORE IT SAVES — the change moves the reach of every nodal officer at the unit at
 * once — and dismissing the dialog leaves the controlled value where it was. `modal` from
 * `App.useApp()`, never a `<Modal>`: themed, assertable, and no census entry.
 */
export const UnitElevationControl = ({
  unit,
  allUnits,
  canSet,
}: {
  unit: OrgUnitDto;
  /** The loaded tree, to name the unit each level acts at. A name outside it is simply omitted. */
  allUnits: readonly OrgUnitDto[];
  canSet: boolean;
}) => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const labelId = useId();
  const noteId = useId();

  const actingAtFor = (elevation: OrgUnitElevation): string | undefined => {
    if (elevation === 'NONE') return undefined;
    const id = actingOrgUnitIdOf({ _id: unit.id, ancestors: unit.ancestors, elevation });
    return allUnits.find((candidate) => candidate.id === id)?.name;
  };

  const save = useMutation({
    mutationFn: (elevation: OrgUnitElevation) => api.put<OrgUnitDto>(`/org-units/${unit.id}/elevation`, { elevation }),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.orgUnits.detail(saved.id), saved);
      /* The tree and the lists carry `elevation` (the marks), and every officer posted there now
         carries a different `actingOrgUnit` (their labels). */
      void queryClient.invalidateQueries({ queryKey: queryKeys.orgUnits.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.officers.all });
      void message.success(`Saved for every nodal officer posted at ${saved.name}`);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /* Between the save and the tree's refetch, show what was saved rather than flicker back. */
  const current: OrgUnitElevation = save.isSuccess && save.data.id === unit.id ? save.data.elevation : unit.elevation;
  const isRoot = unit.ancestors.length === 0;

  const confirmChange = (next: OrgUnitElevation): void => {
    if (next === current) return;
    void modal.confirm({
      title: `${unit.name}: ${elevationSentence(next, actingAtFor(next))}?`,
      content: `Every nodal officer posted at ${unit.name} will act at this level from their next request. Constables posted there are not affected.`,
      okText: 'Change it',
      cancelText: 'Keep it',
      onOk: () => save.mutate(next),
    });
  };

  return (
    <Card data-testid="unit-elevation">
      <CardHeader className="p-4">
        <CardTitle className="text-sm" id={labelId}>
          Nodal access level
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 p-4 pt-0">
        {canSet && !isRoot ? (
          <Select
            className="w-full max-w-md"
            aria-labelledby={labelId}
            aria-describedby={noteId}
            value={current}
            loading={save.isPending}
            disabled={save.isPending}
            onChange={confirmChange}
            options={ORG_UNIT_ELEVATIONS.map((value) => ({
              value,
              label: elevationSentence(value, actingAtFor(value)),
            }))}
          />
        ) : (
          <span className="inline-flex items-center gap-2 text-sm" data-testid="unit-elevation-value">
            <ElevationMark elevation={current} actingAt={actingAtFor(current)} />
            {isRoot ? 'Its own level' : elevationSentence(current, actingAtFor(current))}
          </span>
        )}
        <p className="text-xs text-muted-foreground" id={noteId}>
          {isRoot
            ? ROOT_SENTENCE
            : `Every nodal officer posted at ${unit.name} acts at this level: they see and manage everything beneath it, and see but cannot change the officers posted at that level itself. Constables are not affected.${canSet ? '' : ' Only a super admin can change it.'}`}
        </p>
      </CardContent>
    </Card>
  );
};

/**
 * The Officers drawer's READ-ONLY view of the officer's posting unit's level, beneath Dashboard
 * access. It reads the unit by id (the drawer holds only the officer's `orgUnitId`).
 *
 * ⚠️ A FAILED READ SAYS SO, WITH A RETRY; while it LOADS nothing renders, because nothing sits
 * below it to shift.
 */
export const UnitElevationReadout = ({ orgUnitId, actingAt }: { orgUnitId: string; actingAt?: string }) => {
  const labelId = useId();
  const unit = useQuery({
    queryKey: queryKeys.orgUnits.detail(orgUnitId),
    queryFn: ({ signal }) => api.get<OrgUnitDto>(`/org-units/${orgUnitId}`, { signal }),
  });

  if (unit.isError) {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm" data-testid="unit-elevation" role="alert">
        <span>Could not load this unit&apos;s nodal access level.</span>
        <Button size="small" onClick={() => void unit.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!unit.data) return null;
  const isRoot = unit.data.ancestors.length === 0;

  return (
    <div className="mt-4 flex flex-col gap-1" data-testid="unit-elevation">
      <span className="text-sm font-medium" id={labelId}>
        Nodal access level of {unit.data.name}
      </span>
      <span
        className="inline-flex items-center gap-2 text-sm"
        data-testid="unit-elevation-value"
        aria-labelledby={labelId}
        role="note"
      >
        <ElevationMark elevation={unit.data.elevation} actingAt={actingAt} />
        {elevationSentence(unit.data.elevation, actingAt)}
      </span>
      <span className="text-xs text-muted-foreground">
        {isRoot
          ? ROOT_SENTENCE
          : `Set for the whole unit on the Organisation page. It applies to every nodal officer posted at ${unit.data.name}, not only this officer.`}
      </span>
    </div>
  );
};
